import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import isEqual from 'lodash.isequal';
import {
  BaseTransaction,
  TransactionAML,
  Response,
  TransactionWarning,
  GeneralLedgerTransaction,
  Pagination,
  TransactionWarningTypeEnum,
} from '@ui/src';
import { ExternalAccount, IncomingTransactionValidator } from '../../types';
import { incomingTransactionsListApi } from '../services';
import { statusesTransactionsApi } from '../../../../store/statuses/services';
import { transactionApi } from '../../../transactions/store/services';
import { WarningsInfo } from '../../../transactions/type';

export interface IncomingTransactionState {
  panelIsOpen: boolean;
  transactionId?: string;
  transaction?: BaseTransaction;
  aml?: TransactionAML;
  validators?: IncomingTransactionValidator;
  warnings?: TransactionWarning[];
  glTransactions?: GeneralLedgerTransaction[];
  externalAccounts?: ExternalAccount[];
  externalPagination?: Pagination;
  warningsInfo?: WarningsInfo;
}

const initialState: IncomingTransactionState = {
  panelIsOpen: true,
  transaction: undefined,
  transactionId: undefined,
};

export const incomingTransactionSlice = createSlice({
  name: 'incomingTransaction',
  initialState,
  reducers: {
    changePanelState: (
      state,
      action: PayloadAction<{ state: boolean; transactionId?: string }>,
    ) => {
      state.panelIsOpen = action.payload.state;

      if (action.payload.state) {
        state.transactionId = action.payload.transactionId;
      } else {
        state.transactionId = undefined;
      }
    },
    setWarnings: (state, action: PayloadAction<TransactionWarning[]>) => {
      state.warnings = action.payload;
    },
    clearIncomingTransactionState: (state) => {
      state.transaction = initialState.transaction;
      state.transactionId = undefined;
      state.panelIsOpen = false;
      state.warnings = undefined;
      state.warningsInfo = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        incomingTransactionsListApi.endpoints.getIncomingTransactionById
          .matchFulfilled,
        (state, action: PayloadAction<{ data: BaseTransaction }>) => {
          const prevTransaction = state.transaction;
          const nextTransaction = action.payload.data;

          if (!prevTransaction) {
            state.transaction = nextTransaction;
          } else if (!isEqual(prevTransaction, nextTransaction)) {
            state.transaction = nextTransaction;
          }
        },
      )
      .addMatcher(
        incomingTransactionsListApi.endpoints.getIncomingTransactionValidators
          .matchFulfilled,
        (
          state,
          action: PayloadAction<{ data: IncomingTransactionValidator }>,
        ) => {
          state.validators = action.payload.data;
        },
      )
      .addMatcher(
        incomingTransactionsListApi.endpoints.getIncomingTransactionWarningsInfo
          .matchFulfilled,
        (state, action: PayloadAction<{ data: WarningsInfo }>) => {
          state.warningsInfo = action.payload.data;
        },
      )
      .addMatcher(
        incomingTransactionsListApi.endpoints.getIncomingTransactionAmlById
          .matchFulfilled,
        (state, action: PayloadAction<Response<TransactionAML>>) => {
          const prevAml = state.aml;
          const nextAml =
            action.payload.data.edges.map((item) => item.node)?.[0] ||
            undefined;

          if (!prevAml) {
            state.aml = nextAml;
          } else if (!isEqual(prevAml, nextAml)) {
            state.aml = nextAml;
          }
        },
      )
      .addMatcher(
        incomingTransactionsListApi.endpoints
          .getTransactionsExternalAccountsList.matchFulfilled,
        (state, action: PayloadAction<Response<ExternalAccount>>) => {
          if (!state.externalAccounts) {
            state.externalAccounts = action.payload?.data.edges?.map(
              (item) => item.node,
            );

            state.externalPagination = action.payload.data.pageInfo;
          }
        },
      )
      .addMatcher(
        incomingTransactionsListApi.endpoints.getIncomingTransactionWarnings
          .matchFulfilled,
        (state, action: PayloadAction<Response<TransactionWarning>>) => {
          const prevWarnings = state.warnings;
          const nextWarnings = action.payload.data.edges.map(
            (item) => item.node,
          );

          if (!prevWarnings) {
            state.warnings = nextWarnings;
          } else if (!isEqual(prevWarnings, nextWarnings)) {
            state.warnings = nextWarnings?.map((item) => {
              const result = { ...item };

              const currentPrevWarning = prevWarnings?.find(
                (prevWarning) => prevWarning?.id === result.id,
              );

              if (
                currentPrevWarning &&
                (currentPrevWarning?.blacklistDoc ||
                  currentPrevWarning?.blacklistItem)
              ) {
                result.blacklistDoc = currentPrevWarning.blacklistDoc;
                result.blacklistItem = currentPrevWarning.blacklistItem;
              }

              if (
                currentPrevWarning &&
                currentPrevWarning.relatedTransactions
              ) {
                result.relatedTransactions =
                  currentPrevWarning?.relatedTransactions;
              }

              return result;
            });
          }
        },
      )
      .addMatcher(
        incomingTransactionsListApi.endpoints.getIncomingTransactionWarning
          .matchFulfilled,
        (
          state,
          action: PayloadAction<{ data: TransactionWarning }> & {
            meta: {
              arg: {
                originalArgs: {
                  warningId: string;
                };
              };
            };
          },
        ) => {
          const nextWarnings = state.warnings?.map((item) => {
            const result = { ...item };

            if (result.id === action.meta.arg.originalArgs.warningId) {
              if (
                action.payload.data.type ===
                TransactionWarningTypeEnum.BLACKLIST
              ) {
                result.blacklistDoc = action.payload.data.blacklistDoc;
                result.blacklistItem = action.payload.data.blacklistItem;
              }

              if (
                action.payload.data.type ===
                TransactionWarningTypeEnum.MONITORING
              ) {
                result.relatedTransactions =
                  action.payload.data.relatedTransactions;
              }
            }

            return result;
          });

          state.warnings = nextWarnings;
        },
      )
      .addMatcher(
        incomingTransactionsListApi.endpoints
          .applyIncomingTransactionWarningAction.matchFulfilled,
        (state, action: PayloadAction<{ data: TransactionWarning }>) => {
          const nextWarnings = state.warnings?.map((item) => {
            let result = { ...item };

            if (action.payload.data.id === result.id) {
              result = {
                ...action.payload.data,
                blacklistDoc: result.blacklistDoc,
                blacklistItem: result.blacklistItem,
                relatedTransactions: result?.relatedTransactions || [],
              };
            }

            return result;
          });

          state.warnings = nextWarnings;
        },
      );

    builder.addMatcher(
      statusesTransactionsApi.endpoints.changeTransactionStatus.matchFulfilled,
      (state, action: PayloadAction<{ data: BaseTransaction }>) => {
        if (state.transaction) {
          const nextTransaction = { ...state.transaction };

          nextTransaction.baseellaStatus = action.payload.data.baseellaStatus;
          nextTransaction.latestQueuedStatuses = action.payload.data
            .latestQueuedStatuses
            ? action.payload.data.latestQueuedStatuses
            : nextTransaction.latestQueuedStatuses;

          state.transaction = nextTransaction;
        }
      },
    );
    builder.addMatcher(
      incomingTransactionsListApi.endpoints.updateIncomingTransaction
        .matchFulfilled,
      (state, action: PayloadAction<{ data: BaseTransaction }>) => {
        state.transaction = { ...state.transaction, ...action.payload.data };
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getGeneralLedgerTransactionValues.matchFulfilled,
      (state, action: PayloadAction<Response<GeneralLedgerTransaction>>) => {
        if (state.transaction) {
          state.glTransactions = action.payload.data.edges.map(
            (item) => item.node,
          );
        }
      },
    );
  },
});

export const { changePanelState, clearIncomingTransactionState, setWarnings } =
  incomingTransactionSlice.actions;

export default incomingTransactionSlice.reducer;
